import { useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  Merge,
  Pencil,
  Plus,
  Tags,
} from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import Dot from '@/components/ui/Dot.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import {
  SectionLayout,
  SectionLayoutSkeleton,
} from '@/components/ui/SectionLayout.tsx';
import {
  SettingsList,
  SettingsListGroupLabel,
  SettingsListRow,
} from '@/components/ui/SettingsList.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import EditCostCategoryModal from '@/features/dashboard/costCategories/components/EditCostCategoryModal.tsx';
import useGetCostCategories from '@/features/dashboard/costCategories/hooks/useGetCostCategories.ts';
import useArchiveCostCategory from '@/features/dashboard/costCategories/hooks/useArchiveCostCategory.ts';
import useUnarchiveCostCategory from '@/features/dashboard/costCategories/hooks/useUnarchiveCostCategory.ts';
import useDeleteCostCategory from '@/features/dashboard/costCategories/hooks/useDeleteCostCategory.ts';
import { CostCategoryDto } from '@/features/dashboard/costCategories/types/CostCategoryDto.ts';
import CostCategoryDeleteButton from '@/features/dashboard/costCategories/components/CostCategoryDeleteButton.tsx';
import CreateCostCategoryModal from '@/features/dashboard/costCategories/components/CreateCostCategoryModal.tsx';
import MergeCostCategoryModal from '@/features/dashboard/costCategories/components/MergeCostCategoryModal.tsx';

const SECTION_DESCRIPTION =
  'Kategoria maluje rozbicie budżetu wyjazdu, dlatego kolor jest tu daną, nie ozdobą. Kategorii w użyciu nie da się usunąć — archiwizuj ją.';

const CostCategoriesList = () => {
  const [editItem, setEditItem] = useState<CostCategoryDto | null>(null);
  const [mergeItem, setMergeItem] = useState<CostCategoryDto | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { data: categories, isLoading } = useGetCostCategories();
  const archiveMutation = useArchiveCostCategory();
  const unarchiveMutation = useUnarchiveCostCategory();
  const deleteMutation = useDeleteCostCategory();
  const { confirmDeleteWithCallback, confirmWithCallback } =
    confirmDialogUseHook();

  const handleArchive = (category: CostCategoryDto) =>
    confirmWithCallback({
      title: 'Zarchiwizuj kategorię kosztów',
      description: (
        <>
          Kategoria <b>{category.name}</b> zniknie z list wyboru, ale zostanie
          na wydatkach, które już ją mają.
        </>
      ),
      confirmText: 'Archiwizuj',
      cancelText: 'Wróć',
      onConfirm: () => archiveMutation.mutate(category.id),
    });

  const handleUnarchive = (category: CostCategoryDto) =>
    confirmWithCallback({
      title: 'Przywróć kategorię kosztów',
      description: (
        <>
          Kategoria <b>{category.name}</b> wróci na listy wyboru w wydatkach.
        </>
      ),
      confirmText: 'Przywróć',
      cancelText: 'Wróć',
      onConfirm: () => unarchiveMutation.mutate(category.id),
    });

  const handleDelete = (category: CostCategoryDto) =>
    confirmDeleteWithCallback({
      title: 'Usuń kategorię kosztów',
      description: (
        <>
          Czy na pewno chcesz trwale usunąć kategorię <b>{category.name}</b>?
        </>
      ),
      confirmText: 'Usuń',
      cancelText: 'Wróć',
      onConfirm: () => deleteMutation.mutate(category.id),
    });

  if (isLoading) {
    return <SectionLayoutSkeleton rows={3} />;
  }

  // `getAll` wraca w kolejności bazy, więc porządek listy ustalamy tutaj.
  const byName = (a: CostCategoryDto, b: CostCategoryDto) =>
    a.name.localeCompare(b.name, 'pl');

  const live =
    categories?.filter((category) => !category.archived).sort(byName) ?? [];
  const archived =
    categories?.filter((category) => category.archived).sort(byName) ?? [];
  const hasCategories = live.length > 0 || archived.length > 0;

  const colorRing = (category: CostCategoryDto) => (
    // Barwa idzie z backendu, więc wchodzi przez `style`; obręcz bierze ją z `currentColor`.
    <Dot size="xl" hollow style={{ color: category.color }} />
  );

  return (
    <>
      <SectionLayout
        title="Kategorie kosztów"
        description={SECTION_DESCRIPTION}
      >
        {hasCategories ? (
          <div className="space-y-6">
            <div>
              {live.length > 0 && (
                <SettingsList>
                  {live.map((category) => (
                    <SettingsListRow
                      key={category.id}
                      leading={colorRing(category)}
                      title={category.name}
                      actions={
                        <>
                          <ActionButton
                            tone="outline"
                            size="toolbar"
                            onClick={() => setEditItem(category)}
                          >
                            <Pencil />
                            Zmień
                          </ActionButton>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <ActionButton
                                tone="outline"
                                size="toolbar"
                                iconOnly
                                aria-label="Archiwizuj"
                                onClick={() => handleArchive(category)}
                              >
                                <Archive />
                              </ActionButton>
                            </TooltipTrigger>
                            <TooltipContent>Archiwizuj</TooltipContent>
                          </Tooltip>
                          {/* Jedyna żywa kategoria nie ma z czym się scalić. */}
                          {live.length > 1 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <ActionButton
                                  tone="outline"
                                  size="toolbar"
                                  iconOnly
                                  aria-label="Scal z inną kategorią"
                                  onClick={() => setMergeItem(category)}
                                >
                                  <Merge />
                                </ActionButton>
                              </TooltipTrigger>
                              <TooltipContent>
                                Scal z inną kategorią
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <CostCategoryDeleteButton
                            category={category}
                            onDelete={() => handleDelete(category)}
                          />
                        </>
                      }
                    />
                  ))}
                </SettingsList>
              )}

              <ActionButton
                tone="outline"
                size="toolbar"
                className={live.length > 0 ? 'mt-3' : undefined}
                onClick={() => setIsCreateOpen(true)}
              >
                <Plus />
                Dodaj kategorię
              </ActionButton>
            </div>

            {archived.length > 0 && (
              <div className="space-y-2">
                <SettingsListGroupLabel>Zarchiwizowane</SettingsListGroupLabel>
                <SettingsList className="bg-neutral-50">
                  {archived.map((category) => (
                    <SettingsListRow
                      key={category.id}
                      muted
                      leading={colorRing(category)}
                      title={category.name}
                      status={
                        <StatusDot
                          tone="neutral"
                          label="Zarchiwizowana"
                          className="text-[12.5px]"
                        />
                      }
                      actions={
                        <>
                          <ActionButton
                            tone="outline"
                            size="toolbar"
                            onClick={() => handleUnarchive(category)}
                          >
                            <ArchiveRestore />
                            Przywróć
                          </ActionButton>
                          <CostCategoryDeleteButton
                            category={category}
                            onDelete={() => handleDelete(category)}
                          />
                        </>
                      }
                    />
                  ))}
                </SettingsList>
              </div>
            )}
          </div>
        ) : (
          <NoItems
            icon={<Tags />}
            title="Brak kategorii kosztów"
            description="Dodaj kategorie, żeby budżet wyjazdu rozbijał się na pozycje w raportach."
          >
            <ActionButton
              tone="outline"
              size="toolbar"
              onClick={() => setIsCreateOpen(true)}
            >
              <Plus />
              Dodaj kategorię
            </ActionButton>
          </NoItems>
        )}
      </SectionLayout>

      {editItem && (
        <EditCostCategoryModal
          costCategory={editItem}
          onClose={() => setEditItem(null)}
        />
      )}
      {isCreateOpen && (
        <CreateCostCategoryModal onClose={() => setIsCreateOpen(false)} />
      )}
      {mergeItem && (
        <MergeCostCategoryModal
          category={mergeItem}
          onClose={() => setMergeItem(null)}
        />
      )}
    </>
  );
};

export default CostCategoriesList;
